import { ApolloClient, ApolloProvider, NormalizedCacheObject } from "@apollo/client"
import { useAuth0 } from "@auth0/auth0-react"
import { Box, ThemeProvider, createTheme } from "@mui/material"
import React, { useContext, useEffect, useState } from "react"
import WifiIcon from "@mui/icons-material/Wifi"
import WifiOff from "@mui/icons-material/WifiOff"
import CloudOffIcon from "@mui/icons-material/CloudOff"
import { Loading } from "./components/Loading"
import { JobProvider } from "./contexts/JobContext"
import { ShiftProvider } from "./contexts/ShiftContext"
import { SiteProvider } from "./contexts/SiteContext"
import { UserProvider } from "./contexts/UserContext"
import SnackbarAlert from "./components/SnackbarAlert"

import NativeLayerContext from "./contexts/NativeLayerContext"
import { retryOfflineMutations } from "./services/offlineMutationService"
import "./styles/globals.css"
import { darkPalette, themeOptions } from "./styles/theme"
import { createApolloClient, createApolloClientWithToken } from "./utils/apolloClient"
import { MobileBrowserRouter } from "./utils/MobileBrowserRouter"
import { EFunctionalityOverrides, ENativeLayerOutgoingEventType } from "./utils/nativeLayerCommunication/interfaces"
import { sendNativeLayerEvent } from "./utils/nativeLayerCommunication/sendNativeLayerEvent"
import { StorageProvider } from "./contexts/StorageContext"
import StorageWarning from "./components/StorageWarning"
import ActionLoader from "./components/ActionLoader"

function nativeAuthMode(queryParams: URLSearchParams) {
    if (queryParams.get("nativeAuth") === "true") {
        return true
    }
    return false
}

const App = () => {
    const { getIdTokenClaims, isLoading, isAuthenticated, loginWithRedirect } = useAuth0()
    const [client, setClient] = useState<ApolloClient<NormalizedCacheObject> | null>(null)
    const nativeLayerContext = useContext(NativeLayerContext)

    //Need to extract it from here
    const [showOfflineModal, setShowOfflineModal] = useState(false)
    const [isLoadingMutations, setIsLoadingMutations] = useState(false)
    const [progress, setProgress] = useState(1)
    const [snackbarOpen, setSnackbarOpen] = useState(false)

    useEffect(() => {
        if (!isLoadingMutations) {
            setProgress(1) // Reset the progress value when loading finishes
        }
    }, [isLoadingMutations])

    const queryStrings = new URLSearchParams(window.location.search)

    // Create a theme instance.
    const theme = createTheme({
        ...themeOptions,
        palette: { ...darkPalette },
    })

    // To be used in contexts that involve a SITE switch - auth / site / shift
    const reinitializeApolloClient = async () => {
        if (client) {
            await client.resetStore()
            client.stop()
        }
        if (
            nativeLayerContext.nativeFunctionalityOverrides?.includes(EFunctionalityOverrides.AUTH) &&
            nativeLayerContext.auth0IdToken
        ) {
            setClient(await createApolloClientWithToken(nativeLayerContext.auth0IdToken.toString()))
        } else {
            setClient(await createApolloClient(getIdTokenClaims))
        }
    }

    useEffect(() => {
        if (!nativeLayerContext.auth0IdToken) {
            return
        }
        createApolloClientWithToken(nativeLayerContext.auth0IdToken.toString()).then((client) => setClient(client))
    }, [nativeLayerContext.auth0IdToken])

    useEffect(() => {
        if (nativeAuthMode(queryStrings)) {
            return
        }
        if (isLoading) {
            return
        }
        if (!isAuthenticated) {
            // Redirect the user to Auth0 login
            loginWithRedirect()
            return
        }
        createApolloClient(getIdTokenClaims).then((client) => setClient(client))
    }, [getIdTokenClaims, isLoading, isAuthenticated, loginWithRedirect])

    //Network connectivity - to be extracted
    useEffect(() => {
        const handleOnlineStatusChange = async () => {
            setShowOfflineModal(false)
            setSnackbarOpen(true)

            if (navigator.onLine && client) {
                setIsLoadingMutations(true)
                const synced = await retryOfflineMutations(client, (currentIndex) => {
                    setProgress(currentIndex)
                })
                if (synced) {
                    setIsLoadingMutations(false)
                }
            }
        }

        const handleOfflineStatusChange = () => {
            setShowOfflineModal(true)
            setSnackbarOpen(true)
        }

        window.addEventListener("online", handleOnlineStatusChange)
        window.addEventListener("offline", handleOfflineStatusChange)

        // Cleanup
        return () => {
            window.removeEventListener("online", handleOnlineStatusChange)
            window.removeEventListener("offline", handleOfflineStatusChange)
        }
    }, [client])

    if (
        !client ||
        (!nativeAuthMode(queryStrings) && isLoading) ||
        (nativeAuthMode(queryStrings) && !nativeLayerContext.auth0IdToken)
    ) {
        if (nativeAuthMode(queryStrings)) {
            sendNativeLayerEvent({ type: ENativeLayerOutgoingEventType.REQUEST_NATIVE_SYNC })
        }
        return <Loading fullScreen />
    }

    return (
        <ApolloProvider client={client}>
            <UserProvider>
                <SiteProvider reinitializeApolloClient={reinitializeApolloClient}>
                    <ShiftProvider reinitializeApolloClient={reinitializeApolloClient}>
                        <StorageProvider>
                            <JobProvider>
                                <ThemeProvider theme={theme}>
                                    <Box
                                        sx={{
                                            background: theme.palette.background.default,
                                            minHeight: `100vh`,
                                            width: `100vw`,
                                        }}
                                    >
                                        {/* Offline CloudOffIcon */}
                                        {showOfflineModal && (
                                            <>
                                                <CloudOffIcon
                                                    fontSize="large"
                                                    sx={{
                                                        position: "absolute",
                                                        top: 20,
                                                        right: 16,
                                                        color: "#fff",
                                                        fontSize: 30,
                                                    }}
                                                />
                                                <SnackbarAlert
                                                    open={snackbarOpen}
                                                    message="You are currently offline. "
                                                    onClose={() => setSnackbarOpen(false)}
                                                    icon={<WifiOff sx={{ fontSize: 20, color: "#fff" }} />}
                                                />
                                            </>
                                        )}

                                        {/* Online ActionLoader */}
                                        {isLoadingMutations && (
                                            <>
                                                <ActionLoader
                                                    value={progress ?? 0}
                                                    isLoadingMutations={isLoadingMutations}
                                                />
                                                <SnackbarAlert
                                                    open={snackbarOpen}
                                                    message="Your Internet connection restored."
                                                    onClose={() => setSnackbarOpen(false)}
                                                    icon={<WifiIcon sx={{ fontSize: 20, color: "#fff" }} />}
                                                />
                                            </>
                                        )}
                                        {/* Storage warning Modals */}
                                        <StorageWarning />
                                        <MobileBrowserRouter />
                                    </Box>
                                </ThemeProvider>
                            </JobProvider>
                        </StorageProvider>
                    </ShiftProvider>
                </SiteProvider>
            </UserProvider>
        </ApolloProvider>
    )
}

export default App
